import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { Observable, of } from 'rxjs';
import * as fromGenerated from '../../_generated';
import * as fromHelper from '../../analytics/analytics.helper';
import { AnalyticsService } from '../../analytics/analytics.service';
import * as fromAuth from '../../auth';
import * as fromFavorites from '../../favorites';
import * as fromLayout from '../../layout';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { AssistantCardComponent } from './assistant-card.component';
import { AssistantsTabsComponent } from './assistants-tabs.component';
import {
  AssistantsPageTab,
  filterAssistantsByTabs,
  resolveSkillCardEditCapability,
  type SkillCardEditAffordance,
} from './assistants.helper';
import { KnowledgeModalComponent } from './knowledge-modal.component';
import { SkillBuilderHostComponent } from './skill-builder-host.component';
import {
  AssistantActions,
  AssistantBuilderModalActions,
  KnowledgeModalActions,
} from './store/assistants.actions';
import {
  assistantsFeature,
  AssistantsState,
  type BuilderRouteMode,
} from './store/assistants.reducer';

type AssistantMobileDrawerAction = 'create-assistant' | 'data-boxes';

@Component({
  selector: 'squadbox-assistants',
  imports: [
    NgClass,
    TranslateModule,
    fromSeriousSystem.ButtonDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.DrawerComponent,
    fromSeriousSystem.OptionMenuComponent,
    fromSeriousSystem.ModalComponent,
    TopBarMobileResponsive,
    AssistantCardComponent,
    KnowledgeModalComponent,
    AssistantsTabsComponent,
    SkillBuilderHostComponent,
  ],
  template: `
    <div
      class="grid grid-rows-[min-content_auto] h-svh w-full relative [grid-template-areas:'topbar'_'content'] bg-gradient-e-vertical"
    >
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          [hideMoreOptionsButton]="false"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
          (moreOptionsClicked)="isMobileDrawerOpened.set(true)"
        >
          <span class="typo-p2 font-semibold text-center w-full block">{{
            'ASSISTANTS.TITLE' | translate
          }}</span>
        </squadbox-top-bar-mobile>
      } @else {
        <header
          class="flex flex-row place-content-end gap-4 py-4 px-6 [grid-area:topbar]"
        >
          <button
            sdButton
            color="neutral"
            variant="outline"
            size="sm"
            (click)="handleClickCreateAssistantCTA()"
          >
            {{ 'ASSISTANTS.CREATE_ASSISTANT' | translate }}
          </button>
        </header>
      }
      <section
        [class.justify-center]="!assistantsForSelectedTab().length"
        [class.tablet-portrait:pt-20]="assistantsForSelectedTab().length"
        class="
        flex flex-col h-full
        w-full max-w-180 mx-auto pb-6
        align-center
        max-h-[calc(100svh - 5rem)]
        overflow-y-auto hide-scrollbar
        [grid-area:content]
      "
      >
        <!-- Assistant Library Header on Desktop -->
        @if (!isMobile() || !assistantsForSelectedTab().length) {
          <div
            class="mx-auto pointer-events-none min-h-14"
            sdIconButton
            variant="icon"
            color="neutral"
            size="lg"
          >
            <svg sdUseNavigationIcon="dashboard"></svg>
          </div>
          <div class="flex flex-col text-center">
            <h1 class="typo-h6 font-semibold">
              {{ 'ASSISTANTS.TITLE' | translate }}
            </h1>
          </div>
        }

        <!-- Tabs -->
        <squadbox-assistants-tabs
          [ngClass]="{
            'pt-6 px-4 sticky top-0 z-10': isMobile(),
            'pt-10': !isMobile(),
          }"
          [tabOptions]="tabOptions()"
          [isMobile]="isMobile()"
          [selectedTabKey]="selectedTabKey()"
        ></squadbox-assistants-tabs>

        <!-- Assistants List -->
        @if (assistantsForSelectedTab().length) {
          <ul class="flex flex-col gap-6 h-full overflow-y-auto hide-scrollbar">
            @for (
              assistant of assistantsForSelectedTab();
              track assistant.uuid
            ) {
              <squadbox-assistant-card
                [class.first:mt-6]="isMobile()"
                [class.first:mt-8]="!isMobile()"
                [class.mx-4]="isMobile()"
                [isMobile]="isMobile()"
                [logo]="assistant.logo"
                [name]="assistant.name"
                [description]="assistant.description"
                [userId]="userId() || ''"
                [ownedBy]="assistant.ownedBy"
                [status]="assistant.status"
                [isAdded]="assistant.isFavorite"
                [isDefaultChat]="assistant.isDefaultChat"
                [editCapability]="assistant.editAffordance.capability"
                [editTooltipKey]="assistant.editAffordance.tooltipKey"
                (addClicked)="handleAddAssistantCTA({ uuid: assistant.uuid })"
                (removeClicked)="
                  handleRemoveAssistantCTA({ uuid: assistant.uuid })
                "
                (editClicked)="
                  handleEditAssistantCTA({
                    uuid: assistant.uuid,
                    builderRoute: getBuilderRoute(assistant.editAffordance),
                  })
                "
                (editKnowledgeClicked)="
                  handleEditKnowledgeCTA({ uuid: assistant.uuid })
                "
                (deleteClicked)="
                  handleDeleteAssistantCTA({
                    uuid: assistant.uuid,
                    name: assistant.name,
                  })
                "
                (newConvoClicked)="handleNewConvoCTA({ uuid: assistant.uuid })"
              ></squadbox-assistant-card>
            }
          </ul>
        } @else {
          <div
            class="
                flex flex-col gap-1 text-center
                [&>p]:typo-p3 [&>p]:text-neutral-500
                m-16
              "
          >
            <p>
              {{ 'ASSISTANTS.SUBTITLE_NO_ASSISTANTS_LINE_1' | translate }}
            </p>
            <p>
              {{ 'ASSISTANTS.SUBTITLE_NO_ASSISTANTS_LINE_2' | translate }}
            </p>
          </div>
          <button
            sdButton
            color="primary"
            size="sm"
            class="mx-auto"
            (click)="handleClickCreateAssistantCTA()"
          >
            {{ 'ASSISTANTS.CREATE_ASSISTANT' | translate }}
          </button>
        }
      </section>

      <!-- More options -->
      @if (isMobile()) {
        <sd-drawer
          [isOpen]="isMobileDrawerOpened()"
          (closeClicked)="isMobileDrawerOpened.set(false)"
        >
          @for (option of mobileDrawerOptions(); track option.label) {
            <sd-option-menu
              [option]="option"
              (triggerAction)="handleMobileDrawerTriggerActionCTA($event)"
            ></sd-option-menu>
          }
        </sd-drawer>
      }
      <sd-modal
        [isModalVisible]="isConfirmDeleteAssistantModalOpened()"
        [modalOptions]="{
          title: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.TITLE' | translate,
          showCloseButton: false,
          actions: [
            {
              label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_CANCEL' | translate,
              action: 'cancel',
              type: 'cancel',
              disabled: false,
            },
            {
              label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE' | translate,
              action: 'delete',
              type: 'delete',
              disabled: false,
            },
          ],
          backdropDismiss: true,
        }"
        (triggerAction)="
          handleConfirmDeleteAssistantModalTriggerActionCTA().action($event)
        "
      >
        <div
          class="w-auto tablet-portrait:w-120"
          innerHTML="{{
            'ASSISTANTS.DELETE_ASSISTANT_MODAL.DESCRIPTION'
              | translate
                : {
                    assistantName:
                      handleConfirmDeleteAssistantModalTriggerActionCTA()
                        .assistantName,
                  }
          }}"
        ></div>
        <p class="mt-2 w-auto tablet-portrait:w-120">
          {{ 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CONVERSATIONS' | translate }}
        </p>
      </sd-modal>

      <!-- Skill builder modal overlay (SB-3325 PR-4, Stage B). The host owns
           the @if, the snapshot binding and both dismissal prompts, so the
           Teammate page can render the same element (SB-3650 / T5, KD-32). -->
      <squadbox-skill-builder-host />

      <!-- Edit knowledge modal (SB-3650 / T3): the knowledge manager's branch
           of the pencil. T2 ships the slice and the handler that opens it; the
           component is created per opening and destroyed on close, which is
           what keeps its transaction id and its staged list per session. -->
      @if (knowledgeModal().kind !== 'closed') {
        <squadbox-knowledge-modal />
      }
    </div>
  `,
})
export class AssistantsPage {
  public readonly tab = input.required<AssistantsPageTab>();

  private readonly assistantsStore = inject(Store<AssistantsState>);
  private readonly favoritesStore = inject(
    Store<fromFavorites.AssistantFavoritesState>,
  );
  private readonly favoriteEntities = this.favoritesStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly translateService = inject(TranslateService);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );

  /** Edit knowledge modal state (SB-3650 / T2 ships it, T3 renders it). */
  public readonly knowledgeModal = this.assistantsStore.selectSignal(
    assistantsFeature.selectKnowledgeModal,
  );
  private readonly assistantEntities = this.assistantsStore.selectSignal(
    assistantsFeature.selectEntities,
  );
  /** SB-3650 / T5 — the builder overlay and both dismissal prompts live here. */
  private readonly builderHost = viewChild(SkillBuilderHostComponent);

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  private readonly assistantsFilteredByTabs = computed(() => {
    // A list, so the selector that leaves out role-read-only skills; the
    // click handlers look skills up in `selectEntities`, which stays raw.
    const listedAssistants = this.assistantsStore.selectSignal(
      assistantsFeature.selectAllListed,
    );

    return filterAssistantsByTabs(listedAssistants(), this.userId());
  });
  public readonly assistantsForSelectedTab = computed(() => {
    const selectedTabKey = this.tab();
    const assistants = this.assistantsFilteredByTabs()[selectedTabKey];
    const favoriteEntities = this.favoriteEntities();
    const canManageKnowledgeByRole = this.canManageKnowledgeByRole();
    const isSuperAdmin = this.isSuperAdmin();
    const userId = this.userId();
    if (assistants && assistants.length > 0) {
      return assistants.map((assistant) => ({
        ...assistant,
        isFavorite: assistant.uuid in favoriteEntities,
        // SB-3650 / KD-2. The pencil's verdict joins the row it belongs to, in
        // lockstep — never a Record keyed by uuid, whose miss would reach a
        // required input as `undefined` with nothing red.
        editAffordance: resolveSkillCardEditCapability(
          assistant,
          userId,
          canManageKnowledgeByRole,
          isSuperAdmin,
        ),
      }));
    }
    return [];
  });
  public readonly tabOptions = computed(() => {
    const assistantsFilteredByTabs = this.assistantsFilteredByTabs();

    return [
      {
        key: 'createdByMe' as AssistantsPageTab,
        label: this.translateService.instant(
          'ASSISTANTS.TABS.CREATED_BY_ME',
        ) as string,
        disabled: assistantsFilteredByTabs.createdByMe.length === 0,
      },
      {
        key: 'sharedWithMe' as AssistantsPageTab,
        label: this.translateService.instant(
          'ASSISTANTS.TABS.SHARED_WITH_ME',
        ) as string,
        disabled: assistantsFilteredByTabs.sharedWithMe.length === 0,
      },
    ];
  });
  public readonly userId = computed(
    () => this.authStore.selectSignal(fromAuth.authFeature.selectUser)()?.id,
  );
  /**
   * SB-3650 / KD-18. The role conjunct of the knowledge-manager capability —
   * `admin` and `owner` only, never `selectIsAdminOrAbove`, which includes
   * `super_admin`. One conjunct of an affordance, never the control.
   */
  private readonly canManageKnowledgeByRole = this.authStore.selectSignal(
    fromAuth.authFeature.selectCanManageKnowledgeByRole,
  );
  private readonly isSuperAdmin = this.authStore.selectSignal(
    fromAuth.authFeature.selectIsSuperAdmin,
  );

  public readonly isMobileDrawerOpened = signal(false);
  public readonly mobileDrawerOptions = signal<fromSeriousSystem.OptionMenu[]>([
    {
      label: this.translateService.instant(
        'ASSISTANTS.CREATE_ASSISTANT',
      ) as string,
      icon: { type: 'uiBasic', icon: 'edit' },
      action: 'create-assistant' as AssistantMobileDrawerAction,
    },
  ]);
  public readonly isConfirmDeleteAssistantModalOpened = signal(false);
  public readonly handleConfirmDeleteAssistantModalTriggerActionCTA = signal<{
    action: (action: string) => void;
    assistantName: string | undefined;
  }>({ action: () => void 0, assistantName: undefined });
  public readonly selectedTabKey = computed(() => {
    const tab = this.tab();
    const tabOptions = this.tabOptions();
    const selectedTabOption = tabOptions.find((option) => option.key === tab);

    if (selectedTabOption && !selectedTabOption.disabled) {
      return selectedTabOption.key;
    }

    return 'createdByMe';
  });

  public handleAddAssistantCTA(event: { uuid: string }): void {
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.favoritesStore.dispatch(
      fromFavorites.AssistantFavoritesActions.addFavorite({
        assistantId: event.uuid,
        assistant,
      }),
    );
  }

  public handleRemoveAssistantCTA(event: { uuid: string }): void {
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.favoritesStore.dispatch(
      fromFavorites.AssistantFavoritesActions.removeFavorite({
        assistantId: event.uuid,
        assistant,
      }),
    );
  }

  public getBuilderRoute(
    editAffordance: SkillCardEditAffordance,
  ): BuilderRouteMode {
    return editAffordance.capability === 'builder'
      ? editAffordance.builderRoute
      : 'owner';
  }

  public handleEditAssistantCTA(event: {
    uuid: string;
    builderRoute: BuilderRouteMode;
  }): void {
    if (event.builderRoute === 'admin') {
      // The admin GET supplies the prompt; the store copy is redacted.
      this.assistantsStore.dispatch(
        AssistantBuilderModalActions.openManagedEdit({ uuid: event.uuid }),
      );
      return;
    }

    // Snapshot the loaded entity at click time and hand the whole AssistantView
    // to the slice, so the open builder is immune to later store churn.
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.assistantsStore.dispatch(
      AssistantBuilderModalActions.openEdit({ assistant }),
    );
  }

  /**
   * The knowledge manager's pencil (SB-3650). Mirrors
   * `handleEditAssistantCTA`: the entity is snapshotted at click time and the
   * whole `AssistantView` handed to the slice, so an open modal is immune to
   * later store churn.
   *
   * **Nothing renders the slice yet** — T3 (squadbox-app#1466) adds the modal.
   * So on this branch the chain terminates here: a knowledge manager clicks the
   * pencil, the slice opens, and no modal appears. That is expected, not a bug
   * to bisect — B1's four tasks merge into one integration branch and reach
   * `main` together (SB-3650 / KD-21), so no user meets the dead click.
   */
  public handleEditKnowledgeCTA(event: { uuid: string }): void {
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.assistantsStore.dispatch(KnowledgeModalActions.open({ assistant }));
  }

  public handleDeleteAssistantCTA(event: { uuid: string; name: string }): void {
    // Capture the full entity at access time (mirrors handleEditAssistantCTA)
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.setupConfirmDeleteAssistantModal(
      assistant.uuid,
      assistant.name,
      assistant,
    );
    this.isConfirmDeleteAssistantModalOpened.set(true);
  }

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  public handleMobileDrawerTriggerActionCTA(action: string): void {
    // Dismiss the bottom sheet on select so it doesn't linger behind the
    // builder modal it opens.
    this.isMobileDrawerOpened.set(false);
    if ((action as AssistantMobileDrawerAction) === 'create-assistant') {
      this.handleClickCreateAssistantCTA();
    }
  }

  public handleClickCreateAssistantCTA(): void {
    this.assistantsStore.dispatch(AssistantBuilderModalActions.openCreate());
  }

  public async handleNewConvoCTA(event: { uuid: string }) {
    const assistant = this.assistantEntities()[event.uuid];
    this.analyticsService
      .sendSkillOpenedEvent({
        source: 'skills_page',
        assistantId: event.uuid,
        ...(assistant ? fromHelper.toSkillLifecycleEvent(assistant) : {}),
      })
      .subscribe();
    await this.routerService.navigateToNewChatWithSkill(event.uuid);
  }

  private setupConfirmDeleteAssistantModal(
    assistantUuid: string,
    assistantName: string,
    assistant: fromGenerated.AssistantView,
  ): void {
    this.handleConfirmDeleteAssistantModalTriggerActionCTA.set({
      assistantName,
      action: (action: string) => {
        switch (action as fromSeriousSystem.ModalAction['type']) {
          case 'delete':
            this.assistantsStore.dispatch(
              AssistantActions.deleteAssistant({
                uuid: assistantUuid,
                assistant,
              }),
            );
            this.isConfirmDeleteAssistantModalOpened.set(false);
            break;
          case 'cancel':
            this.isConfirmDeleteAssistantModalOpened.set(false);
            break;
        }
        this.handleConfirmDeleteAssistantModalTriggerActionCTA.set({
          action: () => void 0,
          assistantName: undefined,
        });
      },
    });
  }

  constructor() {
    effect(() => {
      const tab = this.tab();
      const tabOptions = this.tabOptions();
      const selectedTabOption = tabOptions.find((option) => option.key === tab);
      if (!selectedTabOption || selectedTabOption.disabled) {
        void this.routerService.navigateToAssistants('createdByMe');
      }
    });
  }

  /**
   * CanDeactivate hook. The prompt, the confirm dialog and the close dispatch
   * all live in `SkillBuilderHostComponent` (SB-3650 / T5, KD-32), so both
   * routes that render the builder behave identically.
   *
   * `true` when the host is not present. Unlike the pre-T5 code this replaced,
   * that branch is not a live case: the host is rendered UNCONDITIONALLY, so it
   * resolves whenever this page has rendered at all, whereas the old
   * `viewChild(AssistantBuilderPage)` sat under an `@if` and was legitimately
   * undefined while the slice was open. If it is ever made conditional, this
   * branch has to close both slices the way the host does.
   */
  public canDeactivate(): Observable<boolean> {
    return this.builderHost()?.canDeactivate() ?? of(true);
  }
}
